import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { AlertTriangle, Info, X } from 'lucide-react'
import type { CodeItemAuthoringMode, CodeItemGroup, CodeItemSubgroup } from '../../types/codeIndex'
import {
  getCodeItemDigits,
  getCompositeGroupStructureErrors,
  getRuleResizeImpact,
  isCompositeCodeItem,
  transformCodeItemResize,
} from '../../utils/compositeCodeUtils'
import { showConfirmModal } from '../ui/ConfirmDialog'
import Button from '../ui/Button'
import { CompositeGroupEditor } from './CompositeGroupEditor'

export type CodeItemEditorMode = 'create' | 'edit'

export type CodeItemEditorValue = {
  title: string
  digits: number
  authoringMode: CodeItemAuthoringMode
  simpleGroupNames: string[]
  subgroups: CodeItemSubgroup[]
}

type CodeItemEditorDialogProps = {
  open: boolean
  mode: CodeItemEditorMode
  code: string
  start: number
  group?: CodeItemGroup | null
  allGroups?: CodeItemGroup[]
  initialValue: CodeItemEditorValue
  autoFocusCompositeAdd?: boolean
  onClose: () => void
  onApply: (value: CodeItemEditorValue) => void | Promise<void>
}

function normalizeDigits(value: number): number {
  return Math.max(1, Math.floor(Number(value)) || 1)
}

function sameValue(left: CodeItemEditorValue, right: CodeItemEditorValue): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

export const CodeItemEditorDialog: React.FC<CodeItemEditorDialogProps> = ({
  open,
  mode,
  code,
  start,
  group = null,
  allGroups = [],
  initialValue,
  autoFocusCompositeAdd = false,
  onClose,
  onApply,
}) => {
  const [value, setValue] = useState<CodeItemEditorValue>(initialValue)
  const [simpleGroupName, setSimpleGroupName] = useState('')
  const [titleTouched, setTitleTouched] = useState(false)
  const simpleGroupInputRef = useRef<HTMLInputElement | null>(null)
  const initialValueRef = useRef(initialValue)
  initialValueRef.current = initialValue

  useEffect(() => {
    if (!open) return
    const initial = initialValueRef.current
    setValue({ ...initial, simpleGroupNames: [...initial.simpleGroupNames], subgroups: initial.subgroups.map(item => ({ ...item })) })
    setSimpleGroupName('')
    setTitleTouched(false)
  }, [code, mode, open])

  const digits = normalizeDigits(value.digits)
  const currentDigits = group ? getCodeItemDigits(group) : digits
  const titleError = value.title.trim() ? null : 'Required.'
  const authoringModeLocked = mode === 'edit' && Boolean(group?.rows.length)
  const structureErrors = value.authoringMode === 'composite'
    ? getCompositeGroupStructureErrors(value.subgroups, digits)
    : []
  const resizePreview = useMemo(() => {
    if (!group) return null
    const currentMode: CodeItemAuthoringMode = isCompositeCodeItem(group) ? 'composite' : 'simple'
    const sourceGroup = currentMode === value.authoringMode
      ? group
      : { ...group, authoringMode: value.authoringMode, subgroups: value.authoringMode === 'composite' ? value.subgroups : [] }
    return transformCodeItemResize(sourceGroup, digits, value.authoringMode === 'composite' ? value.subgroups : [])
  }, [digits, group, value.authoringMode, value.subgroups])
  const ruleImpact = useMemo(() => {
    if (!group || !resizePreview?.result) return { affectedRules: [], safelyRemappedRules: 0 }
    return getRuleResizeImpact(allGroups, group.codeItemPos.code, digits, resizePreview.result.patternMap, currentDigits)
  }, [allGroups, currentDigits, digits, group, resizePreview])
  const affectedOptions = resizePreview?.result?.impact.affectedOptions || []
  const errors = [...structureErrors, ...(resizePreview?.errors || [])]
  const changed = mode === 'create' || !sameValue(value, initialValue)
  const isReduction = Boolean(group && digits < currentDigits)
  const affectedCount = affectedOptions.length + ruleImpact.affectedRules.length

  const addSimpleGroup = () => {
    const name = simpleGroupName.trim()
    if (!name || value.simpleGroupNames.some(item => item.toLowerCase() === name.toLowerCase())) return
    setValue(current => ({ ...current, simpleGroupNames: [...current.simpleGroupNames, name] }))
    setSimpleGroupName('')
    simpleGroupInputRef.current?.focus()
  }

  const handleApply = async () => {
    setTitleTouched(true)
    if (titleError || errors.length > 0 || !changed) return
    if (isReduction && affectedCount > 0) {
      const confirmed = await showConfirmModal({
        title: 'Apply digit reduction?',
        description: `Reducing Code ${code} from ${currentDigits} to ${digits} digits will leave ${affectedOptions.length} option(s) and ${ruleImpact.affectedRules.length} rule(s) pending correction. Save and Submit will remain blocked until they are fixed. No concrete value will be deleted.`,
        confirmText: 'Apply reduction',
        cancelText: 'Cancel',
        icon: 'warning',
      })
      if (!confirmed) return
    }
    await onApply({ ...value, digits })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="code-item-editor-title">
      <DialogTitle id="code-item-editor-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <div>
          <div className="text-lg font-semibold text-gray-900">{mode === 'create' ? 'Create Code Item' : 'Edit Code Item'}</div>
          <div className="text-xs font-normal text-gray-500">Code {code} · Positions {start}-{start + digits - 1}</div>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close Code Item editor">
          <X className="h-4 w-4" />
        </button>
      </DialogTitle>

      <DialogContent dividers>
        <div className="grid grid-cols-[8rem_1fr_9rem] items-start gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            Code Item
            <input value={code} readOnly className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm text-gray-600" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            Title
            <input
              value={value.title}
              onChange={event => setValue(current => ({ ...current, title: event.target.value }))}
              onBlur={() => setTitleTouched(true)}
              aria-label="Code Item title"
              aria-invalid={titleTouched && Boolean(titleError)}
              className={`rounded border px-2 py-1.5 text-sm ${titleTouched && titleError ? 'border-[#B3261E]' : 'border-[#B7C6D8]'}`}
            />
            {titleTouched && titleError && <span className="text-[11px] text-[#B3261E]" role="alert">{titleError}</span>}
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            Number of digits
            <input
              type="number"
              min={1}
              value={digits}
              onChange={event => setValue(current => ({ ...current, digits: normalizeDigits(Number(event.target.value)) }))}
              aria-label="Number of digits"
              className="rounded border border-[#B7C6D8] px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        <fieldset className="mt-4">
          <legend className="mb-2 text-xs font-medium text-gray-600">Authoring mode</legend>
          <div className="inline-flex overflow-hidden rounded border border-[#B7C6D8]">
            {(['simple', 'composite'] as CodeItemAuthoringMode[]).map(authoringMode => (
              <button
                key={authoringMode}
                type="button"
                disabled={authoringModeLocked}
                onClick={() => setValue(current => ({
                  ...current,
                  authoringMode,
                  subgroups: authoringMode === 'composite' ? current.subgroups : [],
                  simpleGroupNames: authoringMode === 'simple' ? current.simpleGroupNames : [],
                }))}
                className={`px-4 py-1.5 text-xs font-medium capitalize ${value.authoringMode === authoringMode ? 'bg-[#DCEBFA] text-[#1D5176]' : 'bg-white text-gray-600'} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {authoringMode}
              </button>
            ))}
          </div>
          {authoringModeLocked && <div className="mt-1 text-[11px] text-gray-500">Authoring mode cannot change while the Code Item contains options.</div>}
        </fieldset>

        {value.authoringMode === 'composite' ? (
          <div className="mt-4">
            <CompositeGroupEditor
              digits={digits}
              subgroups={value.subgroups}
              onChange={subgroups => setValue(current => ({ ...current, subgroups }))}
              autoFocusAdd={autoFocusCompositeAdd}
            />
          </div>
        ) : mode === 'create' ? (
          <div className="mt-4 rounded border border-[#D6E4F2] bg-[#F8FBFF] p-3">
            <div className="mb-2 text-xs font-medium text-gray-600">Initial visual groups (optional)</div>
            <div className="flex gap-2">
              <input
                ref={simpleGroupInputRef}
                value={simpleGroupName}
                onChange={event => setSimpleGroupName(event.target.value)}
                onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addSimpleGroup() } }}
                aria-label="New visual group name"
                placeholder="Group name"
                className="min-w-0 flex-1 rounded border border-[#B7C6D8] bg-white px-2 py-1.5 text-sm"
              />
              <Button onClick={addSimpleGroup} disabled={!simpleGroupName.trim()}>Add</Button>
            </div>
            {value.simpleGroupNames.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {value.simpleGroupNames.map(name => (
                  <span key={name.toLowerCase()} className="inline-flex items-center gap-1 rounded-full border border-[#B7D4F4] bg-white px-2 py-1 text-xs text-[#1D5176]">
                    {name}
                    <button type="button" onClick={() => setValue(current => ({ ...current, simpleGroupNames: current.simpleGroupNames.filter(item => item !== name) }))} aria-label={`Remove ${name}`}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 flex items-start gap-2 rounded border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-[#1D5176]">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            Visual groups for simple Code Items continue to be managed directly from the table.
          </div>
        )}

        {group && digits !== currentDigits && (
          <div className={`mt-4 flex items-start gap-2 rounded border px-3 py-2 text-xs ${affectedCount > 0 ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-blue-100 bg-blue-50 text-[#1D5176]'}`}>
            {affectedCount > 0 ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : <Info className="mt-0.5 h-4 w-4 shrink-0" />}
            <div>
              <div>Code Item range will change from {currentDigits} to {digits} positions. Later Code Items will be realigned automatically.</div>
              {resizePreview?.result && resizePreview.result.impact.safelyRemappedOptions > 0 && <div className="mt-1">{resizePreview.result.impact.safelyRemappedOptions} option(s) can be remapped safely.</div>}
              {ruleImpact.safelyRemappedRules > 0 && <div>{ruleImpact.safelyRemappedRules} rule(s) can be remapped safely.</div>}
              {affectedCount > 0 && <div className="mt-1 font-medium">{affectedOptions.length} option(s) and {ruleImpact.affectedRules.length} rule(s) will require correction before Save/Submit.</div>}
              {affectedOptions.slice(0, 3).map(issue => <div key={`${issue.rowId}:${issue.reason}`} className="mt-1">{issue.reason}</div>)}
              {ruleImpact.affectedRules.slice(0, 2).map(issue => <div key={`${issue.sourceGroupIndex}:${issue.sourceRowIndex}`} className="mt-1">{issue.reason}</div>)}
            </div>
          </div>
        )}

        {errors.length > 0 && (
          <div className="mt-3 rounded border border-[#F2B8B5] bg-[#FFF4F4] px-3 py-2 text-xs text-[#B3261E]" role="alert">
            {Array.from(new Set(errors)).map(error => <div key={error}>{error}</div>)}
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button disabled={!changed || Boolean(titleError) || errors.length > 0} onClick={() => void handleApply()}>
          {mode === 'create' ? 'Create' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CodeItemEditorDialog
